/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate} from 'react-router-dom'
import {useFormik} from 'formik'
import axios from 'axios'
import {apiUrl} from '../../../../api-utils/constantVariables'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(128, 'Maximum 128 symbols')
    .required('Password is required'),
})

export function Login() {
  const [loading, setLoading] = useState(false)
  const initialValues = {email: '', password: ''}
  const navigate = useNavigate()
  const [error, setError] = useState('')

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      setLoading(true)
      setError('')
      try {
        const res = await axios.post(`${apiUrl}/admin/auth/login`, {
          email: values.email,
          password: values.password,
        })
        const payload = res.data?.data
        if (!payload) {
          setError('Unexpected response')
          return
        }
        if (payload.stage === 'totp_required' && payload.login_token) {
          navigate('/auth/2fa', {state: {login_token: payload.login_token}})
          return
        }
        if (
          (payload.stage === 'authenticated' || payload.stage === 'must_change_password') &&
          payload.token
        ) {
          const admin = {
            ...(payload.admin || {}),
            JWToken: payload.token,
            must_change_password: !!payload.must_change_password,
          }
          localStorage.setItem('admin', JSON.stringify(admin))
          window.dispatchEvent(new Event('shrido-admin-profile-changed'))
          if (payload.must_change_password) {
            navigate('/account/settings')
          } else {
            navigate('/dashboard')
          }
          return
        }
        setError('Unexpected login state')
      } catch (err: any) {
        const msg =
          err?.response?.data?.message || err?.response?.data?.error || 'Invalid email or password.'
        setError(typeof msg === 'string' ? msg : 'Invalid email or password.')
      } finally {
        setLoading(false)
      }
    },
  })

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      <div className='fv-row mb-8'>
        {error ? (
          <div style={{color: 'red'}}>
            <p>{error}</p>
          </div>
        ) : null}
        <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
        <input
          placeholder='Email'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent',
            {'is-invalid': formik.touched.email && formik.errors.email},
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
          type='email'
          name='email'
          autoComplete='username'
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <span role='alert'>{formik.errors.email}</span>
          </div>
        )}
      </div>

      <div className='fv-row mb-3'>
        <label className='form-label fw-bolder text-dark fs-6 mb-0'>Password</label>
        <input
          type='password'
          autoComplete='current-password'
          {...formik.getFieldProps('password')}
          className={clsx(
            'form-control bg-transparent',
            {
              'is-invalid': formik.touched.password && formik.errors.password,
            },
            {
              'is-valid': formik.touched.password && !formik.errors.password,
            }
          )}
        />
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>

      <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
        <div />
        <Link to='/auth/forgot-password' className='link-primary'>
          Forgot Password ?
        </Link>
      </div>

      <div className='d-grid mb-10'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-primary'
          disabled={formik.isSubmitting || !formik.isValid || loading}
        >
          <span className='indicator-label'>{loading ? 'Please wait…' : 'Continue'}</span>
        </button>
      </div>
    </form>
  )
}
